import { alphaToEmu, emuToAlpha } from '@src/slide/infrastructure/module/independent/unit';
import { STSchemeColorVal } from '@tencent/slide-sdk-model';
import { shallowOmit } from '@src/slide/infrastructure/module/independent/object/shallow-omit';

/**
 * 根据fraction值来计算当前的颜色。 fraction值范围  0f-1f
 *
 * @param startColor  开始颜色  rgb(r,g,b,a)或16进制
 * @param endColor    结束颜色  rgb(r,g,b,a)或16进制
 * @param fraction    百分比
 */
export function gradientColor(startColor, endColor, fraction) {
    let colorArray = getColorRGB(startColor);
    const [redStart, greenStart, blueStart, alphaStart] = colorArray;

    colorArray = getColorRGB(endColor);
    const [redEnd, greenEnd, blueEnd, alphaEnd] = colorArray;

    const redDifference = redEnd - redStart;
    const greenDifference = greenEnd - greenStart;
    const blueDifference = blueEnd - blueStart;
    const alphaDifference = alphaEnd - alphaStart;

    const redCurrent = Math.round(redStart + (fraction * redDifference));
    const greenCurrent = Math.round(greenStart + (fraction * greenDifference));
    const blueCurrent = Math.round(blueStart + (fraction * blueDifference));
    const alphaCurrent = Math.round(alphaStart + (fraction * alphaDifference));

    return `rgb(${redCurrent},${greenCurrent},${blueCurrent},${alphaCurrent})`;
}

export function gradientColorExtendsAlpha(startColor, endColor, fraction) {
    let colorArray = getColorRGB(startColor);
    const [redStart, greenStart, blueStart, alphaStart] = colorArray;

    colorArray = getColorRGB(endColor);
    const [redEnd, greenEnd, blueEnd] = colorArray;
    // let alphaEnd =colorArray[3];

    const redDifference = redEnd - redStart;
    const greenDifference = greenEnd - greenStart;
    const blueDifference = blueEnd - blueStart;
    // let alphaDifference = alphaEnd - alphaStart;

    const redCurrent = Math.round(redStart + (fraction * redDifference));
    const greenCurrent = Math.round(greenStart + (fraction * greenDifference));
    const blueCurrent = Math.round(blueStart + (fraction * blueDifference));
    const alphaCurrent = alphaStart;

    return `rgb(${redCurrent},${greenCurrent},${blueCurrent},${alphaCurrent})`;
}

// 颜色16进制转RGBA方法
function colorRgb(colorHex: string) {
    let sColor = colorHex.toLowerCase();
    // 十六进制颜色值的正则表达式
    const reg = /^#([0-9a-fA-f]{3}|[0-9a-fA-f]{6}|[0-9a-fA-f]{8})$/;
    // 如果是16进制颜色
    if (sColor && reg.test(sColor)) {
        if (sColor.length === 4) {
            let sColorNew = '#';
            for (let i = 1; i < 4; i += 1) {
                sColorNew += sColor.slice(i, i + 1).concat(sColor.slice(i, i + 1));
            }
            sColor = sColorNew;
        }
        // 处理六位的颜色值
        const sColorChange: number[] = [];
        for (let i = 1; i < sColor.length; i += 2) {
            const d = sColor.slice(i, i + 2);
            sColorChange.push(parseInt(`0x${d}`, 16));
        }
        if (sColorChange.length === 4) {
            sColorChange[3] = sColorChange[3] / 255;
        } else {
            sColorChange.push(1);
        }
        return sColorChange;
    }
    return sColor;
}

/**
 * RGB转16进制
 * @param colorRgb
 */
// eslint-disable-next-line no-shadow
export function rgb2Hex(colorRgb) {
    const color = colorRgb;
    if (color.toLowerCase().startsWith('rgb')) {
        const index = color.indexOf('(') + 1;
        const transColor = color.substring(index, color.length - 1).split(',');
        if (transColor.length >= 3) {
            let colorValue = '';
            let alpha = 1;
            for (let i = 0; i < transColor.length; i++) {
                if (i === 3) {
                    alpha = transColor[i];
                } else {
                    let trans = parseInt(transColor[i], 10).toString(16);
                    trans = trans.length === 1 ? (`0${trans}`) : trans;
                    colorValue += trans;
                }
            }
            if (alpha === 1) {
                return `#${colorValue}`;
            }
            return `#${colorValue}${parseInt(`${alpha * 255}`, 10).toString(16)}`;
        }
    }
    return color;
}

/**
 * 转换颜色RGB值，返回一个数组
 * @param color
 */
export function getColorRGB(color: string) {
    let transColor: any = colorRgb(color);
    if (transColor === color) {  // 转换失败，继续转换
        if (color.startsWith('rgb')) {
            const index = color.indexOf('(') + 1;
            transColor = color.substring(index, color.length - 1).split(',');
            transColor = transColor.map(item => Number(item));
            if (transColor.length === 3) {
                transColor.push(1);
            }
        }
    }
    return transColor;
}

/**
 * 16进制或rgb的颜色转成 {colorValue :'20ffff' , alpha : 1 }形式
 *
 * @param color  16进制或rgb的颜色
 * @returns {colorValue :'20ffff' , alpha : 0.8}类型
 */
export function color2HexAndAlpha(color) {
    if (color) {
        const reg = /^#([0-9a-fA-f]{3}|[0-9a-fA-f]{6}|[0-9a-fA-f]{8})$/;
        // 如果是16进制颜色
        if (reg.test(color)) {
            if (color.length === 4) {
                let sColorNew = '#';
                for (let i = 1; i < 4; i += 1) {
                    sColorNew += color.slice(i, i + 1).concat(color.slice(i, i + 1));
                }
                color = sColorNew;
            }
            if (color.length === 7) {
                return { colorValue: color.substring(1, 7), alpha: 1 };
            }
            if (color.length === 9) {
                const alpha = parseInt(color.substring(7, 9), 16) / 255;
                return { colorValue: color.substring(1, 7), alpha };
            }
        } else if (color.toLowerCase().startsWith('rgb')) {
            const index = color.indexOf('(') + 1;
            const transColor = color.substring(index, color.length - 1).split(',');
            if (transColor.length >= 3) {
                return getColorValueAndAlpha(transColor);
            }
        }
    }
    return { colorValue: color, alpha: 1 };
}

function getColorValueAndAlpha(transColor) {
    let colorValue = '';
    let alpha = 1;
    for (let i = 0; i < transColor.length; i++) {
        if (i === 3) {
            alpha = parseFloat(transColor[i]);
        } else {
            let trans = parseInt(transColor[i], 10).toString(16);
            trans = trans.length === 1 ? (`0${trans}`) : trans;
            colorValue += trans;
        }
    }
    return { colorValue, alpha };
}

/**
 *
 * @param colorValue
 * @param alpha
 * @returns {string}
 */
export function hexAndAlpha2Color(colorValue, alpha) {
    let alpha16 = '';
    if (typeof alpha === 'number' && alpha <= 1 && alpha >= 0) {
        alpha16 = parseInt(`${255 * alpha}`, 10).toString(16);
        if (alpha16.length === 1) {
            alpha16 = `0${alpha16}`;
        }
    }
    return `#${colorValue.replace('#', '')}${alpha16}`;
}

/**
 * 传入ooxml的srgbClr，组合出十六进制带有alpha格式的色值
 * @param srgbClr
 * @returns {string}
 */
export function getEgColorChoiceRgba(srgbClr) {
    const rgb = srgbClr?.val;
    const emu = srgbClr?.egColorTransform?.[0]?.alpha?.val ?? 100000;
    if (rgb) {
        return hexAndAlpha2Color(rgb, emuToAlpha(emu));
    }
    return rgb;
}

/**
 * 传入rgba(red, green, blue, alpha) 导出十六进制颜色值
 * @param color
 * @returns {string}
 */
export function rgbaToHex(color) {
    // 将颜色字符串转换为数组
    const colorArr = color.replace(/[^\d,.]/g, '').split(',');
    // 将alpha值转换为十六进制
    let alpha;
    if (colorArr[3] || colorArr[3] === 0) {
        alpha = Math.round(parseFloat(colorArr[3]) * 255).toString(16);
    } else {
        alpha = 'ff';
    }
    // 将红、绿、蓝三个通道的值转换为十六进制
    const hex = colorArr.slice(0, 3).map((c) => {
        const hex = parseInt(c).toString(16);
        return hex.length === 1 ? `0${hex}` : hex;
    })
        .join('');
    // 如果alpha值不为1，则添加到hex值的末尾
    return alpha === 'ff' ? `#${hex}` : `#${hex}${alpha}`;
}

/**
 * 传入rgba(red, green, blue, alpha)或者十六进制颜色值，组合出ooxml的srgbClr
 * @param colorHex 十六进制色值/rgba色值 例如： #ffffff/rgba(0, 0, 0, 1)
 * @param egColorTransformMap IEGColorTransform 例如 { lumMod: 100, lumOff: 100 }
 * @returns {string}
 */
export function rgbaToEgColorChoice(colorHex, egColorTransformMap = {}) {
    const lowerCaseColor = colorHex.toLowerCase().replace(/ /g, '');
    let sColor = colorHex;
    // rgba(red, green, blue, alpha)颜色值的正则表达式
    const rgba = /^rgba?\((\d{1,3}),(\d{1,3}),(\d{1,3})(,\d*(\.\d+)?)?\)$/;
    if (lowerCaseColor && rgba.test(lowerCaseColor)) {
        sColor = rgbaToHex(sColor);
    }
    // 十六进制不带#的，补全
    if (!sColor?.startsWith('#')) {
        sColor = `#${sColor}`;
    }
    // 十六进制颜色值的正则表达式
    const reg = /^#([0-9a-fA-f]{3}|[0-9a-fA-f]{6}|[0-9a-fA-f]{7}|[0-9a-fA-f]{8})$/;

    const egColorChoice: any = {
        val: sColor,
        egColorTransform: [{}],
    };

    // 如果有egColorTransform
    if (Object.keys(egColorTransformMap)?.length) {
        Object.keys(egColorTransformMap).forEach((p) => {
            egColorChoice.egColorTransform[0][p] = { val: egColorTransformMap[p] };
        });
    }

    if (reg.test(sColor)) {
        // 如果十六进制颜色值包含alpha
        if ([8, 9].includes(sColor.length)) {
            const alpha = sColor.slice(7);
            egColorChoice.val = sColor.slice(0, 7);
            egColorChoice.egColorTransform[0].alpha = { val: alphaToEmu((parseInt(alpha, 16) / 255).toFixed(2)) };
        } else {
            egColorChoice.egColorTransform[0].alpha = { val: 100000 };
        }
    }

    egColorChoice.val = egColorChoice.val.replace(/#/, '');
    if (!Object.keys(egColorChoice.egColorTransform[0]).length) {
        delete egColorChoice.egColorTransform;
    }
    return egColorChoice;
}

/**
 * 传入主题色值，组合出ooxml的schemeClr
 * @param colorScheme 主题色值 例如： 'accent1' 或者 STSchemeColorVal
 * @param egColorTransformMap IEGColorTransform 例如 { lumMod: 100, lumOff: 100 }
 * @returns {string}
 */
export function schemeToEgColorChoice(colorScheme, egColorTransformMap = {}) {
    const egColorChoice: any = {
        val: typeof colorScheme === 'number' ? colorScheme : STSchemeColorVal[colorScheme],
        egColorTransform: [{}],
    };

    // 如果有egColorTransform
    if (Object.keys(egColorTransformMap)?.length) {
        Object.keys(egColorTransformMap).forEach((p) => {
            egColorChoice.egColorTransform[0][p] = { val: egColorTransformMap[p] };
        });
    }

    if (!Object.keys(egColorChoice.egColorTransform[0]).length) {
        delete egColorChoice.egColorTransform;
    }
    return egColorChoice;
}

/**
 * 判断传入的字符串是主题色值还是十六进制色值
 * 返回对应的ColorDmlA
 * @param color  主题色值或者（十六进制色值/rgba色值） 例如： 'accent1' 或者 （#ffffff/rgba(0, 0, 0, 1)）
 * @param egColorTransformMap IEGColorTransform 例如 { lumMod: 100, lumOff: 100 }
 * @returns {{srgbClr: string}|{schemeClr: string}}
 */
export function colorStrToColorDmlA(color, egColorTransformMap = {}) {
    // 图表有使用color为对象的数据如：color: { colorType: 'schema', colorValue: 'accent1', lumMod: 100 }
    if (typeof color === 'object' && color.colorType === 'scheme') {
        return {
            schemeClr: schemeToEgColorChoice(
                color.colorValue?.toLowerCase(),
                // 移除非egColorTransform的属性
                shallowOmit(color, 'colorType', 'colorValue'),
            ),
        };
    }

    if (STSchemeColorVal[color?.toLowerCase()] || STSchemeColorVal[color]) {
        //  主题色的key并不是全都是小写，比如folHlink和phClr
        const schemeKey = STSchemeColorVal[color?.toLowerCase()] ? color?.toLowerCase() : color;
        // 属于主题枚举中的一个
        return { schemeClr: schemeToEgColorChoice(schemeKey, egColorTransformMap) };
    }
    // 其他场景 rgba || 十六进制
    return { srgbClr: rgbaToEgColorChoice(color, egColorTransformMap) };
}
/**
 * 传入主题色值返回对应的ColorDmlA
 * @param color  主题色值 如STSchemeColorVal.accent1
 * @param egColorTransformMap IEGColorTransform 例如 { lumMod: 100, lumOff: 100 }
 * @returns {{srgbClr: string}|{schemeClr: string}}
 */
export function colorValueToColorDmlA(colorValue, egColorTransformMap = {}) {
    return { schemeClr: schemeToEgColorChoice(colorValue, egColorTransformMap) };
}

export function getColorNoAlpha(color) {
    if (!color) return '';
    const colorObj = color2HexAndAlpha(color);
    let colorNoAlpha = colorObj.colorValue;
    if (!colorNoAlpha.startsWith('#')) {
        colorNoAlpha = `#${colorNoAlpha}`;
    }
    return colorNoAlpha;
}

// todo: 改成ts，把复制粘贴的util切到这
export const rgba2hex = function (rgba) {
    // convert rgb to hex
    const colors = rgba.toString().match(/\d+(\.\d+)?/g); // 把 x,y,z,a 推送到 color 数组里
    let alpha = 1;
    if (typeof colors[3] !== 'undefined') {
        alpha = Number(colors[3]);
    }
    if (alpha === 0) {
        return '';
    }
    let hex = '#';
    if (alpha === 1) {
        for (let i = 0; i < 3; i++) {
            hex += (`0${Number(colors[i]).toString(16)}`).slice(-2);
        }
    } else {
        for (let i = 0; i < 3; i++) {
            hex += (`0${Number(colors[i]).toString(16)}`).slice(-2);
        }
        hex += (alpha * 255).toString(16).substring(0, 2);
    }

    return hex;
};

export function hexToRgba(hex: string, alpha = 1) {
    const reg = /^#([0-9A-f]{3}|[0-9A-f]{6}|[0-9A-f]{8})$/;
    if (!reg?.test(hex)) {
        return '';
    }
    if (/^#[0-9A-f]{3}$/.test(hex)) {
        const converted = hex.match(/\w/g)?.map(d => d + d)
            .join('');
        hex = converted ? `#${converted}` : hex;
    }
    const [r, g, b] = hex.match(/\w\w/g)?.map(x => parseInt(x, 16)) || [];
    return `rgba(${r},${g},${b},${alpha})`;
}

// rgb字符串转对象 例如：将颜色字符串rgb(78, 167, 46)转换为{r: 78, g: 167, b:46}的格式
export function rgbStrToObject(rgbStr: string): { r: number; g: number; b: number } | null {
    // 匹配rgb(整数, 整数, 整数)格式的正则表达式
    const rgbRegex = /^rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/;
    const match = rgbStr.match(rgbRegex);

    // 如果没有匹配到，则返回null
    if (!match) {
        return null;
    }

    // 将匹配到的RGB值转换为整数，并构造返回对象
    return {
        r: parseInt(match[1], 10),
        g: parseInt(match[2], 10),
        b: parseInt(match[3], 10),
    };
}

export function normalizeRgba(rgba: string): string | undefined {
    // 正则表达式用于匹配rgba字符串中的各个分量
    const match = rgba.match(/^rgba\((-?\d+),\s*(-?\d+),\s*(-?\d+),\s*(-?[\d.]+)\)$/);
    if (!match) return;

    // 提取RGBA的各个分量
    const [r, g, b, a] = [match[1], match[2], match[3], match[4]];

    // 转换分量为数字，并检查范围
    const red = Math.min(255, Math.max(0, parseInt(r, 10)));
    const green = Math.min(255, Math.max(0, parseInt(g, 10)));
    const blue = Math.min(255, Math.max(0, parseInt(b, 10)));
    const alpha = Math.min(1, Math.max(0, parseFloat(a)));

    // 返回格式化的rgba字符串
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}
